import { useState, useEffect, useCallback } from 'react'
import { tagsApi } from '@api/endpoints/tags'
import type { TagDto, TagCategoryDto, TagType } from '@features/adventures/types/adventure-editor.types'

const CACHE_KEY = 'all_tags_cache'
const CACHE_TIMESTAMP_KEY = 'all_tags_cache_timestamp'
const CACHE_DURATION = 60 * 60 * 1000 // 1 hour in milliseconds

interface CachedData {
  categories: TagCategoryDto[]
  tagMap: Map<string, TagDto>
  timestamp: number
}

/**
 * Custom hook to fetch and cache adventure tags with automatic refresh
 * Tags are cached in localStorage and refreshed every hour
 * @param enabled - Whether to fetch tags (default: true). Set to false for unauthenticated users.
 */
export function useCachedTags(enabled = true) {
  const [tags, setTags] = useState<Map<string, TagDto>>(new Map())
  const [categories, setCategories] = useState<TagCategoryDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Check if cached data is still valid
   */
  const isCacheValid = useCallback((): boolean => {
    try {
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)
      if (!timestamp) return false

      const cacheAge = Date.now() - parseInt(timestamp, 10)
      return cacheAge < CACHE_DURATION
    } catch {
      return false
    }
  }, [])

  /**
   * Load tags from localStorage cache
   */
  const loadFromCache = useCallback((): CachedData | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY)

      if (!cached || !timestamp) return null

      const parsed = JSON.parse(cached)
      const tagMap = new Map<string, TagDto>()

      // Build tag map from categories
      parsed.forEach((category: TagCategoryDto) => {
        category.tags?.forEach((tag: TagDto) => {
          tagMap.set(tag.id, tag)
        })
      })

      return {
        categories: parsed,
        tagMap,
        timestamp: parseInt(timestamp, 10),
      }
    } catch (err) {
      console.error('Error loading tags from cache:', err)
      return null
    }
  }, [])

  /**
   * Save tags to localStorage cache
   */
  const saveToCache = useCallback((categories: TagCategoryDto[]) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(categories))
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
    } catch (err) {
      console.error('Error saving tags to cache:', err)
    }
  }, [])

  /**
   * Fetch tags from API
   */
  const fetchTags = useCallback(async (forceRefresh = false) => {
    // Check cache first if not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      const cached = loadFromCache()
      if (cached) {
        setCategories(cached.categories)
        setTags(cached.tagMap)
        setIsLoading(false)
        return
      }
    }

    // Fetch from API - get all tag types we need
    try {
      setIsLoading(true)
      setError(null)

      // Fetch all tag categories from new endpoint
      // Returns object: { "Interests": [...], "GenderIdentity": [...], "Adventures": [...], "LookingFor": [...] }
      const categoriesResponse = await tagsApi.getAllTagCategories()

      console.log('Raw API response keys:', Object.keys(categoriesResponse))
      console.log('Raw API response:', categoriesResponse)

      // Flatten the response object into a single array
      const allCategories: TagCategoryDto[] = []
      Object.values(categoriesResponse).forEach((categoryArray) => {
        if (Array.isArray(categoryArray)) {
          allCategories.push(...categoryArray)
        }
      })

      console.log('Flattened categories:', allCategories.length, 'categories')
      console.log('Sample category:', allCategories[0])

      // Count by tag type for logging
      const counts = {
        interests: allCategories.filter((c) => c.tagType === 1).length,
        gender: allCategories.filter((c) => c.tagType === 2).length,
        adventures: allCategories.filter((c) => c.tagType === 3).length,
        lookingFor: allCategories.filter((c) => c.tagType === 4).length,
        total: allCategories.length,
      }

      console.log('Fetched tag categories by type:', counts)
      console.log('All tagTypes found:', [...new Set(allCategories.map(c => c.tagType))])

      const fetchedCategories = allCategories
      const tagMap = new Map<string, TagDto>()

      // Build tag map from categories
      fetchedCategories.forEach((category) => {
        category.tags?.forEach((tag) => {
          tagMap.set(tag.id, tag)
        })
      })

      setCategories(fetchedCategories)
      setTags(tagMap)
      saveToCache(fetchedCategories)
    } catch (err) {
      console.error('Error fetching tags:', err)
      setError('Failed to load tags')

      // Try to use cached data as fallback even if expired
      const cached = loadFromCache()
      if (cached) {
        setCategories(cached.categories)
        setTags(cached.tagMap)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isCacheValid, loadFromCache, saveToCache])

  /**
   * Get tag by ID
   */
  const getTag = useCallback(
    (tagId: string): TagDto | undefined => {
      return tags.get(tagId)
    },
    [tags]
  )

  /**
   * Get multiple tags by IDs
   */
  const getTags = useCallback(
    (tagIds: string[]): TagDto[] => {
      return tagIds.map((id) => tags.get(id)).filter((tag): tag is TagDto => tag !== undefined)
    },
    [tags]
  )

  /**
   * Get categories by TagType
   */
  const getCategoriesByType = useCallback(
    (tagType: TagType): TagCategoryDto[] => {
      return categories.filter((category) => category.tagType === tagType)
    },
    [categories]
  )

  /**
   * Get all adventure categories (TagType.Adventures)
   */
  const getAdventureCategories = useCallback((): TagCategoryDto[] => {
    return categories.filter((category) => category.tagType === 3) // TagType.Adventures
  }, [categories])

  /**
   * Manually refresh tags from API
   */
  const refresh = useCallback(() => {
    return fetchTags(true)
  }, [fetchTags])

  // Initial load
  useEffect(() => {
    if (enabled) {
      fetchTags()
    } else {
      setIsLoading(false)
    }
  }, [enabled, fetchTags])

  // Set up automatic refresh interval
  useEffect(() => {
    if (!enabled) return

    const interval = setInterval(
      () => {
        if (!isCacheValid()) {
          fetchTags()
        }
      },
      5 * 60 * 1000
    ) // Check every 5 minutes if cache needs refresh

    return () => clearInterval(interval)
  }, [enabled, fetchTags, isCacheValid])

  return {
    tags,
    categories,
    isLoading,
    error,
    getTag,
    getTags,
    getCategoriesByType,
    getAdventureCategories,
    refresh,
  }
}
